import { useMemo, useRef, useState } from 'react'

import { Code } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import {
  SilentCancelSubmitError,
  useCrudModalForm,
} from '~/hooks/useCrudModalForm'
import { useListBgMasters } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { TradingPartnerFormFields } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/TradingPartnerFormFields'
import { useCreateTradingPartner } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useCreateTradingPartner'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'
import { isDuplicateTradingPartnerName } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/duplicateName'
import {
  type TradingPartnerFormInput,
  type TradingPartnerFormValues,
  buildTradingPartnerFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/schema'
import { resolveTradingPartnerDetailedError } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/serverError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const TRADING_PARTNER_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.tradingPartner.serverError.alreadyExists',
  [Code.FailedPrecondition]:
    'page:adminOcctoMaster.tradingPartner.serverError.crossMasterAlreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.tradingPartner.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.tradingPartner.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.tradingPartner.serverError.invalidArgument',
}

export const CreateTradingPartnerModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateTradingPartner()
  // AC-06 (spec AC-OMT-020): 名称完全一致の重複警告 dialog 用。 list 取得が
  // 未完了 / エラーの場合は fail-open (dialog をスキップして直接保存)。
  const { data: partnerListData, isError: partnerListIsError } =
    useListTradingPartners()
  const { data: bgListData } = useListBgMasters()

  const existingBgCodeSet = useMemo(() => {
    const codes = new Set<string>()
    if (bgListData !== undefined) {
      for (const bg of bgListData.items) {
        codes.add(bg.bgCode)
      }
    }
    return codes
  }, [bgListData])

  const schema = useMemo(
    () => buildTradingPartnerFormSchema(existingBgCodeSet),
    [existingBgCodeSet],
  )

  const [duplicateNameConfirmOpen, setDuplicateNameConfirmOpen] =
    useState(false)
  // AC-06 (spec AC-OMT-020): 重複警告 dialog のユーザー回答を onSubmit 内で
  // await するための resolver。 dialog の「登録」/「キャンセル」ボタンが
  // これを呼んで onSubmit の続きを再開させる。
  const duplicateConfirmResolverRef = useRef<
    ((proceed: boolean) => void) | null
  >(null)

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    isDirty,
  } = useCrudModalForm<TradingPartnerFormInput, TradingPartnerFormValues>({
    schema,
    defaultValues: {
      name: '',
      shortName: '',
      accounts: [
        {
          areaCode: AreaCode.UNSPECIFIED,
          code: '',
          codeName: '',
          // AC-OMT-030: 新規コードの代表フラグ既定 true。
          isRepresentative: true,
          weekdayContacts: [],
          holidayContacts: [],
        },
      ],
    },
    // AC-06 (spec AC-OMT-020): 名称完全一致の重複判定は Zod validation
    // 通過後の onSubmit (= handleSubmit の onValid) 内で行う。 これにより
    // フォームに Zod error が残っている状態で保存を押しても、 onSubmit へは
    // 到達せず (onInvalid 経路に流れる) 重複ダイアログは出ない。
    // fail-open (list 未取得 / エラー) の場合は判定せず直接保存する。
    onSubmit: async (values) => {
      if (partnerListData !== undefined && !partnerListIsError) {
        if (isDuplicateTradingPartnerName(partnerListData.items, values.name)) {
          const proceed = await new Promise<boolean>((resolve) => {
            duplicateConfirmResolverRef.current = resolve
            setDuplicateNameConfirmOpen(true)
          })
          if (!proceed) {
            throw new SilentCancelSubmitError()
          }
        }
      }
      await mutateAsync({
        name: values.name,
        shortName: values.shortName,
        accounts: values.accounts.map((a) => ({
          areaCode: a.areaCode,
          code: a.code,
          codeName: a.codeName,
          isRepresentative: a.isRepresentative,
          weekdayContacts: a.weekdayContacts.map((c) => ({
            department: c.department,
            name: c.name !== '' ? c.name : undefined,
            phone: c.phone,
            email: c.email !== '' ? c.email : undefined,
          })),
          holidayContacts: a.holidayContacts.map((c) => ({
            department: c.department,
            name: c.name !== '' ? c.name : undefined,
            phone: c.phone,
            email: c.email !== '' ? c.email : undefined,
          })),
        })),
      })
    },
    onClose,
    customErrorKeys: TRADING_PARTNER_ERROR_KEYS,
    unknownErrorKey: 'page:adminOcctoMaster.tradingPartner.serverError.unknown',
    resolveDetailedError: resolveTradingPartnerDetailedError,
  })

  const handleConfirmDuplicateName = (): void => {
    setDuplicateNameConfirmOpen(false)
    duplicateConfirmResolverRef.current?.(true)
  }

  const handleCancelDuplicateName = (): void => {
    setDuplicateNameConfirmOpen(false)
    duplicateConfirmResolverRef.current?.(false)
  }

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design/admin/occto.html #tp-edit-modal: width:min(960px,94vw) 相当の
      // 広幅モーダル。 chakra dialog recipe に 960px 丁度の named size は無い
      // (xs→384/sm→448/md→448/lg→672/xl→896、 実測値)。 "2xl" は ecloud/chakra の
      // dialog recipe に size variant が存在せず maxWidth:none (= 実測で 100vw に
      // 展開、 既存 CsvImportModal 等の "2xl" 指定も同様に無効化されている) になる
      // ため使わず、 実際に maxW を制約する named size のうち最も広い "xl" (896px,
      // 実測) を採用する。
      size="xl"
      title={t('page:adminOcctoMaster.tradingPartner.modal.createTitle')}
    >
      <TradingPartnerFormFields
        control={control}
        errors={errors}
        setValue={setValue}
      />
      {/* AC-06 (spec AC-OMT-020): 名称完全一致の重複警告。 「登録」で続行、
          「キャンセル」で dialog を閉じてモーダルに戻る (保存はしない)。 */}
      <ConfirmDialog
        body={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmBody',
        )}
        confirmLabel={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmLabel',
        )}
        isOpen={duplicateNameConfirmOpen}
        onCancel={handleCancelDuplicateName}
        onConfirm={handleConfirmDuplicateName}
        title={t(
          'page:adminOcctoMaster.tradingPartner.form.duplicateNameConfirmTitle',
        )}
      />
    </CrudModalLayout>
  )
}
